/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { createSelector } from "@reduxjs/toolkit";
import { AppState } from "../../";
import { encode as base64Encode } from "base-64";
import { IfilterObj } from "../types";
import { constStr, DasModelNames } from "../../../globals/constants";

const EMPTY_ARRAY: string[] = [];
const DAS_MODEL_VALUES = DasModelNames.map((model) => model.value);

export const deviceNamesSelector = (state: AppState) => state.filter.deviceHostNames;
export const devicesSelector = (state: AppState) => state.filter;

export const deviceSiteGroupSelector = (state: AppState) => state.filter.selectedDeviceSiteGroup;
export const deviceNameSelector = (state: AppState) => state.filter.selectedDeviceName;
export const deviceSiteSelector = (state: AppState) => state.filter.selectedDeviceSite;
export const deviceTypeSelector = (state: AppState) => state.filter.selectedDeviceType;
export const deviceStatusSelector = (state: AppState) => state.filter.selectedDeviceStatus;
export const deviceConfigStatusSelector = (state: AppState) => state.filter.selectedDeviceConfigStatus;
export const severityTypeSelector = (state: AppState) => state.filter.selectedSeverityType;
export const deviceVersionSelector = (state: AppState) => state.filter.selectedDeviceVersion;
export const dataGridSortSelector = (state: AppState) => state.helpers.sortModel;
export const deviceMaintenanceModeSelector = (state: AppState) => state.filter.maintenanceMode;
export const selectedGeoRegionSelector = (state: AppState) => state.admin.selectedGeoRegion.name || "";
export const selectedGeoRegionIdsSelector = (state: AppState) => state.admin.selectedGeoRegionIds || EMPTY_ARRAY;

export const selectedGeoRegionSiteGroupSelector = (state: AppState) => state.admin.selectedGeoRegion;
export const deviceKpiSyncSelector = (state: AppState) => state.filter.pmCollected;
export const deviceLabelsSelector = (state: AppState) => state.filter.selectedLabels;
export const deviceRegionFilterIdsSelector = (state: AppState) => state.filter.regionFilterIds || EMPTY_ARRAY;

export const urlParameterSelector = createSelector(
  deviceSiteGroupSelector,
  deviceNameSelector,
  deviceSiteSelector,
  deviceTypeSelector,
  deviceStatusSelector,
  deviceConfigStatusSelector,
  severityTypeSelector,
  deviceVersionSelector,
  deviceMaintenanceModeSelector,
  selectedGeoRegionSelector,
  selectedGeoRegionIdsSelector,
  deviceRegionFilterIdsSelector,
  deviceKpiSyncSelector,
  deviceLabelsSelector,
  // Optional list of geo region names that can be provided via selector props
  (_state: AppState, selectedGeoRegions?: string[] | undefined) => selectedGeoRegions,

  (
    siteGroup: string,
    name,
    site,
    type,
    status,
    configstatus,
    severity,
    version,
    maintenanceMode,
    geoRegionName,
    geoRegionIds,
    regionFilterIds,
    pmCollected,
    labels,
    selectedGeoRegions
  ) => {
    const params = new URLSearchParams();
    if (name) {
      params.append("name", name);
    }
    if (site) {
      params.append("site", site);
    }
    // params.append("sort", sortModel || "swVersion,desc");

    const hasSelectedGeoRegions = Array.isArray(selectedGeoRegions) && selectedGeoRegions.length > 0;
    const effectiveRegionIds = regionFilterIds && regionFilterIds.length > 0 ? regionFilterIds : geoRegionIds;
    const isEnableFilter =
      siteGroup ||
      type ||
      status ||
      configstatus ||
      severity ||
      version ||
      maintenanceMode ||
      geoRegionName ||
      (effectiveRegionIds && effectiveRegionIds.length > 0) ||
      pmCollected ||
      labels ||
      hasSelectedGeoRegions;
    if (isEnableFilter) {
      const paramsString: IfilterObj = { type: "AND", operator: "", fieldName: "", value: "", predicates: [] };

      if (siteGroup && siteGroup === constStr.ungrouped) {
        // Ungrouped devices are handled via siteGroupId only; region selection is ignored here.
        paramsString.predicates.push({ type: "", operator: "LIKE", fieldName: "siteGroupId", value: "99999999%", predicates: [{}] });
      } else {
        if (siteGroup && siteGroup !== "ALLDEVICES") {
          if (siteGroup === constStr.ungrouped) {
            paramsString.predicates.push({ type: "", operator: "LIKE", fieldName: "siteGroupId", value: "99999999%", predicates: [{}] });
          } else {
            paramsString.predicates.push({ type: "", operator: "EQUAL", fieldName: "siteGroupId", value: siteGroup, predicates: [{}] });
          }
        }

        if (hasSelectedGeoRegions) {
          // When a list of geo regions is provided (e.g. from Network Overview's GeoRegionFilter),
          // use FIND_IN with a comma-separated value list.
          paramsString.predicates.push({
            type: "",
            operator: "IN",
            fieldName: "geoRegionId",
            value: geoRegionIds!.join(","),
            predicates: [{}],
          });
        } else if (effectiveRegionIds && effectiveRegionIds.length > 0) {
          paramsString.predicates.push({ type: "", operator: "IN", fieldName: "geoRegionId", value: effectiveRegionIds.join(","), predicates: [{}] });
        } else if (!!geoRegionName) {
          // Fallback to single-region filter from admin state
          paramsString.predicates.push({ type: "", operator: "EQUAL", fieldName: "geoRegionId", value: "all", predicates: [{}] });
        }
      }

      if (status) {
        if (status === "0" || status === 0) {
          paramsString.predicates.push({ type: "", operator: "NE", fieldName: "status", value: "19", predicates: [{}] });
        } else {
          paramsString.predicates.push({ type: "", operator: "EQUAL", fieldName: "status", value: status, predicates: [{}] });
        }
      }
      if (configstatus) {
        if (configstatus === "Mismatch") {
          paramsString.predicates.push({ type: "", operator: "NE", fieldName: "goldenAuditFailCount", value: "0", predicates: [{}] });
        } else {
          paramsString.predicates.push({ type: "", operator: "EQUAL", fieldName: "configurationStatus", value: configstatus, predicates: [{}] });
        }
      }
      if (type) {
        paramsString.predicates.push({ type: "", operator: "LIKE", fieldName: "modelName", value: type, predicates: [{}] });
      }
      if (version) {
        paramsString.predicates.push({ type: "", operator: "EQUAL", fieldName: "swVersion", value: version, predicates: [{}] });
      }
      if (labels && labels.length > 0) {
        paramsString.predicates.push({ type: "", operator: "FIND_IN", fieldName: "labelNames", value: labels.join(","), predicates: [{}] });
      }
      if (pmCollected) {
        paramsString.predicates.push({
          type: "",
          operator: pmCollected === "true" ? "is_true" : "is_false",
          fieldName: "pmCollected",
          value: "",
          predicates: [{}],
        });

        // KPI Sync Error is not applicable for DAS models; exclude DAS rows explicitly.
        if (pmCollected === "false") {
          DAS_MODEL_VALUES.forEach((modelName) => {
            paramsString.predicates.push({
              type: "",
              operator: "NE",
              fieldName: "modelName",
              value: modelName,
              predicates: [{}],
            });
          });
        }
      }
      if (maintenanceMode) {
        paramsString.predicates.push({
          type: "",
          operator: maintenanceMode === "true" ? "is_true" : "is_false",
          fieldName: "maintenanceMode",
          value: maintenanceMode === "true" ? "1" : "0",
          predicates: [{}],
        });
      }

      const base64EncodedStr = base64Encode(JSON.stringify(paramsString));
      if (paramsString?.predicates.length > 0) {
        base64EncodedStr && params.append("filters", base64EncodedStr);
      }
      // console.log("Filter :>> ", decode(base64EncodedStr));
    }
    return params;
  }
);
